import { daysUntil } from "@/shared/format";
import type { BillingMe, Rail } from "./api/billing.types";

/**
 * O QUE SE OFERECE A QUEM BATEU NO TETO — a decisão, sem uma tela por perto.
 *
 * ── A REGRA DO PRODUTO, ESCRITA UMA VEZ ───────────────────────────────────
 *
 * Um limite atingido nunca termina numa frase. Ou há um adicional para comprar,
 * ou há um plano para escolher — e qual dos dois depende do TRILHO, não do
 * recurso: num plano pago os adicionais vendem-se por bloco ao preço do plano;
 * num teste (ou de graça) não se vendem adicionais nenhuns, e o caminho é
 * escolher um plano.
 *
 * Está aqui e não dentro do componente porque são três telas que perguntam o
 * mesmo — limites, canais, equipe — e porque uma decisão sobre dinheiro que só
 * existe dentro de um `<template>` não tem como ter teste.
 *
 * ── E O TERCEIRO CASO, QUE É O HONESTO ────────────────────────────────────
 *
 * Um plano pago cujo recurso não tem preço (`unitCents === 0`) não é "de graça":
 * é o interruptor de quem não vende aquele adicional self-service. Aí a única
 * coisa verdadeira é dizer isso e apontar para os planos — inventar um botão
 * "aumentar limite" que o servidor recusaria é pior do que a frase.
 */
export type LimitOfferKind = "extras" | "plan" | "none";

/**
 * Os trilhos em que alguém está mesmo a pagar. `trial` e `free` não são — e desde
 * 28/09/2026 o único que paga é o gateway: não há mais trilho manual.
 */
export function isPaidRail(rail: Rail | null | undefined): boolean {
	return rail === "gateway";
}

export function limitOfferKind(input: {
	rail: Rail | null | undefined;
	hasPlan: boolean;
	sellable: boolean;
	planWaived?: boolean;
}): LimitOfferKind {
	if (!input.hasPlan || (!isPaidRail(input.rail) && !input.planWaived)) return "plan";
	return input.sellable ? "extras" : "none";
}

/** Para onde o botão da oferta leva. Uma tabela, para não haver duas. */
export const OFFER_ROUTE: Readonly<Record<LimitOfferKind, string>> = {
	extras: "/billing/limits",
	plan: "/subscribe",
	none: "/billing",
};

/**
 * TETO ZERO É ILIMITADO — e não "nenhum".
 *
 * `professional` e `scale` gravam `max_channels = 0`, e o servidor lê isso como
 * "sem teto". Um medidor a desenhar "3 / 0 · 100% usado" sobre um recurso sem
 * teto nenhum é a tela a inventar uma parede — e um campo de adicional ao lado
 * dela é a oferta de comprar mais do que já é infinito.
 *
 * `unlimited` chega do servidor quando ele o manda; a base zero é a mesma coisa
 * dita pelo dado que sempre existiu, e é o que mantém a tela certa contra um
 * servidor de antes do marcador.
 */
export function isUnlimited(limit: { base: number; unlimited?: boolean }): boolean {
	return limit.unlimited === true || limit.base === 0;
}

/**
 * JÁ TEM ASSINATURA — e por isso a grade de planos é a porta errada.
 *
 * `POST /v3/billing/plan` é a ENTRADA: pode abrir um Checkout e criar a
 * assinatura. Quem já tem uma faz outra pergunta — "o que acontece se eu
 * trocar?" — cuja resposta é o proporcional, a data em que vale e o que não
 * cabe no plano-alvo, e cuja rota é `POST /v3/billing/plan/change`.
 *
 * A mesma condição que `SubscriptionActions` usa para desenhar o botão de
 * trocar, escrita aqui para que as duas telas não possam discordar sobre quem
 * já assina.
 */
export function alreadySubscribed(me: BillingMe | null | undefined): boolean {
	const subscription = me?.subscription ?? null;
	if (!subscription) return false;
	if (subscription.planWaived) return true;
	if (subscription.rail === "trial" || subscription.rail === "free") return false;
	// No gateway, mesmo a R$0 — o GRATUITO que já cadastrou o cartão —, sobe-se
	// pela troca, que cobra o proporcional na assinatura que a Stripe já tem.
	return subscription.status !== "canceled";
}

/**
 * Quantos dias de calendário o teste ainda tem, no fuso do workspace — 0 é "termina
 * hoje" —, ou `null` quando acabou. O FIM é o do servidor (`daysLeft` 0 é o relógio
 * dele já passado); a contagem é a do dia que a tela escreve ao lado (QA 16).
 */
export function trialDaysLeft(trial: { endsAt: string; daysLeft: number }): number | null {
	return trial.daysLeft === 0 ? null : Math.max(0, daysUntil(trial.endsAt));
}
